import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getErrorMessage } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import {
    getSelectedCategoryPath,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductCarouselProductStyles } from "@insite/content-library/Components/ProductCarouselProduct";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ADIRecommendedProductCarouselProduct from "@NA/Components/ProductDetail/ADIRecommendedProductCarouselProduct";
import recommendedProduct from "@NA/Handlers/ADIRecommendedProductHandler";
import { CustomSettingCollectionModel } from "@NA/Store/CustomTypeModel/CustomSettingCollectionModel";
import ADIColors from "@NA/Utils/ADIColors";
import { useEmblaCarousel } from "embla-carousel/react";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getSelectedProductPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import categoryRecommendedProduct from "@NA/Handlers/ADICategoryRecommendedProductHandler";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import {
    getCategoriesDataView,
    getCategoryState,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import Link from "@insite/mobius/Link";
import { getGaTagforL1L2 } from "@NA/Utils/GtmUtility";
import loadCarouselProducts from "@NA/Overrides/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";

const enum fields {
    title = "title",
    scenario = "scenario",
    carouselType = "carouselType",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    relatedProductType = "relatedProductType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    maxNumberOfColumns = "maxNumberOfColumns",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
}

interface OwnProps extends WidgetProps {
    categoryId: string;
    fields: {
        [fields.title]: string;
        [fields.scenario]: string;
        [fields.carouselType]: string;
        [fields.displayProductsFrom]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.relatedProductType]: string;
        [fields.numberOfProductsToDisplay]: number;
        [fields.seedWithManuallyAssigned]: string;
        [fields.maxNumberOfColumns]: number;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
    };
}

const mapStateToProps = (state: CustomApplicationState, ownProps: OwnProps) => {
    const cart = getCurrentCartState(state);
    const billToState = getBillToState(state, cart?.value?.billToId);
    const billToStateId = getBillToState(state, state.context.session.billToId).value;
    const session = state.context.session;
    const productPath = getSelectedProductPath(state) || productPathValue(state);
    const recommenderProductproduct = getProductStateByPath(state, productPath);
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath).value;
    const categoriesDataView = getCategoriesDataView(state);
    const category = getCategoryState(state, ownProps.categoryId).value;
    let newProudctCategory =
        categoriesDataView.value &&
        categoriesDataView.value?.filter(item => item.name && item.name.toLocaleLowerCase() === "new product");
    const cateName =
        catalogPageState?.canonicalPath != null && catalogPageState?.canonicalPath.split("/").length > 2
            ? catalogPageState?.canonicalPath.split("/")[3]
            : "";
    let newProductPath = null;
    if (newProudctCategory && newProudctCategory.length > 0 && cateName) {
        newProductPath = `${newProudctCategory[0].path}/new-${cateName}`;
    }

    return {
        errorMessage: getErrorMessage(state, ownProps.id),
        pageType: getCurrentPage(state).type,
        billToState,
        billToStateId,
        catalogPageState,
        category,
        isAuthenticated: session && (session.isAuthenticated || session.rememberMe) && !session.isGuest,
        settingsCollection: getSettingsCollection(state) as unknown as CustomSettingCollectionModel,
        recommenderProductNumber: recommenderProductproduct.value?.productNumber,
        recommendedProductsIsLoading: state.ADIGlobalData.PdpRecommendeProductsStore.isLoading,
        isLoadingState: state.ADIGlobalData.CategoryRecommendeProductsStore.isLoading,
        newProductPath,
    };
};

const productPathValue = (state: ApplicationState) => {
    const location = getLocation(state);
    if (location.pathname.toLowerCase().startsWith("/content/")) {
        return "";
    } else {
        return location.pathname;
    }
};

const mapDispatchToProps = {
    loadCarouselProducts,
    recommendedProduct,
    categoryRecommendedProduct,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext &
    HasProduct;

export interface ProductCarouselStyles {
    warper?: InjectableCss;
    newwarper?: InjectableCss;
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    urlText?: InjectableCss;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
}

export const productCarouselStyles: ProductCarouselStyles = {
    warper: {
        css: css`
            margin-bottom: 50px;
            position: relative;
        `,
    },
    newwarper: {
        css: css`
            margin-bottom: 50px;
            position: relative;
            padding: 35px 15px;
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: start;
            margin-bottom: 10px;            
        `,
    },
    urlText: {
        css: css`
            margin-bottom: 12px;
            > span {
                font-size: 14px;
                font-weight: bold;
                font-style: normal;
                line-height: normal;
                letter-spacing: normal;
                color: ${getColor("text.main")} !important;
                text-transform: uppercase;
                &:hover {
                    text-decoration: underline;
                }
            }
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            position: relative;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                        position: absolute;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            min-height: 6px;
            transform: rotate(180deg);
            background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/newicons/arrow-carousel-next-rd.svg)
                no-repeat;
            border: 0;
            width: 28px !important;
            height: 28px !important;
            opacity: 1;
            margin-bottom: 0;
            background-size: 28px 28px !important;
            &:disabled {
                background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/ArrowCarouselright-2.svg)
                    no-repeat;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        right: 0%;
                        top: 31%;
                        display: none;
                    `,
                    css`
                        top: 9%;
                        right: 10%;
                    `,
                    css`
                        top: -12%;
                        right: 12%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                        position: absolute;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            min-height: 6px;
            background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/newicons/arrow-carousel-next-rd.svg)
                no-repeat;
            border: 0;
            width: 28px !important;
            height: 28px !important;
            opacity: 1;
            margin-bottom: 0;
            background-size: 28px 28px !important;
            &:disabled {
                background: url(https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/ArrowCarouselright-2.svg)
                    no-repeat;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        right: 10%;
                        top: 15%;
                        display: none;
                    `,
                    css`
                        top: 9%;
                        right: 7%;
                    `,
                    css`
                        top: 9%;
                        right: 7%;
                    `,

                    null,
                    null,
                ])}
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            flex-basis: 20%;
            max-width: 20%;
            padding: 0 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 33.333%;
                        max-width: 33.333%;
                    `,
                    css`
                        flex-basis: 20%;
                        max-width: 20%;
                    `,
                    css`
                        flex-basis: 20%;
                        max-width: 20%;
                    `,
                ])}
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0px !important;
            display: flex;
            flex-direction: column;
            border: 1px solid ${ADIColors.gray7};
            border-radius: 5px;
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        align-items: center;
                        flex-flow: row;
                    `,
                    css`
                        display: flex;
                        align-items: center;
                        flex-flow: row;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: 24px;
            position: absolute;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 93%;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    dotButton: {
        css: css`
            min-height: 7px;
            max-width: 55px;
            height: 3px;
            width: 10%;
            padding: 0;
            margin: 0 4px;
            border: 0px;
            border-radius: 2px;
            background: ${getColor("common.border")};
            background-color: ${ADIColors.gray16};
            border: 1px solid ${ADIColors.gray16};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0 0;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            &:hover {
                background: ${ADIColors.white};
            }
            &:active {
                background-color: ${ADIColors.darkBlue1};
                border: 1px solid ${ADIColors.darkBlue1};
            }
            &:focus {
                outline: none;
            }
        `,
    },
};

const styles = productCarouselStyles;

const ADICategoryRecommendedProductCarousel: React.FC<Props> = ({
    id,
    theme,
    fields,
    errorMessage,
    categoryRecommendedProduct,
    catalogPageState,
    billToStateId,
    shellContext,
    product,
    settingsCollection,
    recommenderProductNumber,
    isLoadingState,
    isAuthenticated,
    newProductPath,
}) => {
    const afterCarousel = React.createRef<HTMLSpanElement>();
    const [slideProducts, setSildeProduct] = React.useState<ProductModel[]>();
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [products, setProducts] = React.useState<ProductModel[]>();
    const [productInfosByProductId, setProductInfosByProductId] = React.useState<Record<string, ProductInfo>>();
    const [categoryName, setCategoryName] = React.useState("");
    const {
        recommenderSettingSettings: { irService_Modeling },
    } = settingsCollection;
    if (fields.scenario === "Trending" && isAuthenticated) {
        return null;
    }

    if (fields.scenario === "UserPick" && !isAuthenticated) {
        return null;
    }

    const recommederProductValue = () => {
        if (product) {
            if (product.variantTypeId == null) {
                return product.productNumber;
            }
        } else {
            return recommenderProductNumber;
        }
    };
    const toTitleCase = (str: string, separator: string) => {
        return str
            .toLowerCase()
            .split("-")
            .map(function (word) {
                return word.charAt(0).toUpperCase() + word.slice(1);
            })
            .join(separator);
    };

    const getCategoryName = (catName: string) => {
        let categoryName;

        switch (catName) {
            case "wire-cable":
            case "tools-hardware":
                categoryName = toTitleCase(catName, " & ");
                break;
            case "data-communications-networking":
                categoryName = "Data Comm & Networking";
                break;
            case "batteries-power-supplies":
                categoryName = "Batteries & Power Supplies";
                break;
            default:
                categoryName = toTitleCase(catName, " ");
                break;
        }

        return catName;
    };

    const getApiCategoryName = (catName: string) => {
        let apicategoryName;

        switch (catName) {
            case "video-surveillance":
                apicategoryName = "Video Surveillance";
                break;
            case "access-control":
                apicategoryName = "Access Control";
                break;
            case "residential-av":
                apicategoryName = "Residential AV";
                break;
            case "pro-av":
                apicategoryName = "Pro AV";
                break;
            case "fire":
                apicategoryName = "Fire";
                break;
            case "intrusion":
                apicategoryName = "Intrusion";
                break;
            case "smart-home":
                apicategoryName = "Smart Home";
                break;
            case "communications":
                apicategoryName = "Communications";
                break;
            case "wire-cable":
                apicategoryName = "Wire Cable";
                break;
            case "data-communications-networking":
                apicategoryName = "Data Comm Networking";
                break;
            case "tools-hardware":
                apicategoryName = "Tools Hardware";
                break;
            case "batteries-power-supplies":
                apicategoryName = "Batteries Power Supplies";
                break;
            case "central-vacuum":
                apicategoryName = "Central Vacuum";
                break;
            default:
                // Handle default case if needed
                break;
        }
        if (apicategoryName) setCategoryName(apicategoryName);

        return apicategoryName;
    };

    React.useEffect(() => {
        const userId = billToStateId?.customerNumber || "";
        const catName: string | undefined =
            catalogPageState?.canonicalPath != null && catalogPageState?.canonicalPath.split("/").length > 2
                ? catalogPageState?.canonicalPath.split("/")[3]
                : "";
        if (!catName) return;
        let categoryName = getCategoryName(catName);

        let apicategoryName = getApiCategoryName(catName);

        if (apicategoryName && !isLoadingState && fields.scenario) {
            categoryRecommendedProduct({
                userId,
                scenario: fields.scenario,
                modeling: irService_Modeling || "",
                categoryName: apicategoryName,
                erpNumber: recommederProductValue() || "",
                onSuccess(result: any) {
                    setProducts(result.products);
                    setProductInfosByProductId(result.productInfosByProductId);
                },
            });
        }
    }, [billToStateId, recommederProductValue(), catalogPageState?.canonicalPath, fields.scenario]);

    const [emblaRef, embla] = useEmblaCarousel({ loop: false });
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(true);

    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        if (embla) {
            setSelectedIndex(embla.selectedScrollSnap());
        }
    };

    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfColumns = fields.maxNumberOfColumns || 4;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 5;
        }

        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [fields.maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [fields.maxNumberOfColumns, windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();
        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !products || products.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            draggable: isTouchDevice(),
            loop: products.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, products]);

    React.useEffect(() => {
        if (products && products.length > 0) {
            const newProductArray: ProductModel[] = [];
            const itreate = slidesToScroll;
            let nextItertor = itreate;
            newProductArray.push(products[0]);
            products.forEach((item, index) => {
                if (index === nextItertor) {
                    newProductArray.push(item);
                    nextItertor = nextItertor + itreate;
                }
            });
            setSildeProduct([...newProductArray]);
        }
    }, [products]);

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    if (!products || products.length === 0) {
        return null;
    }

    const title = fields.scenario === "Trending" ? `Trending In ${categoryName}` : fields.title;
    const showBrandBlocks = fields.showBrandName && products.some(o => !!o.brand);

    const onClickDot = (i: number) => {
        if (embla) {
            embla.scrollTo(i);
            setSelectedIndex(i);
        }
    };

    const scrollPrev = () => {
        if (embla) {
            if (selectedIndex !== 0) {
                if (selectedIndex - 1 === 0) {
                    setCanScrollPrev(false);
                }
                else {
                    setCanScrollPrev(true);
                }
                setCanScrollNext(true);
                setSelectedIndex(prev => prev - 1);
                embla && embla.scrollPrev();
            }
        }
    };

    const scrollNext = () => {
        if (embla && slideProducts) {
            if (selectedIndex < slideProducts?.length - 1) {
                if (selectedIndex < slideProducts?.length - 1 && (selectedIndex + 1 === slideProducts?.length - 1)) {
                    setCanScrollNext(false);
                }
                else {
                    setCanScrollNext(true);
                }
                setCanScrollPrev(true);
                setSelectedIndex(prev => prev + 1);
                embla && embla.scrollNext();
            }
        }
    };

    const newStyle = fields.scenario && fields.scenario.toLowerCase() === "new" ? styles.newwarper : styles.warper;

    return (
        <StyledWrapper {...newStyle}>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            <StyledWrapper
                css={css`
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    position: relative;
                    margin-top: 10px;
                `}
            >
                <Typography {...styles.titleText}>{title}</Typography>
                {fields.scenario && fields.scenario.toLowerCase() === "new" && newProductPath && (
                    <StyledWrapper
                        css={css`
                            display: flex;
                        `}
                    >
                        <Link {...styles.urlText} href={newProductPath}
                            data-ga-promo={getGaTagforL1L2(title, "", "shop-all",)}>
                            {translate("Shop All")}
                        </Link>
                    </StyledWrapper>
                )}
                <StyledWrapper
                    css={css`
                        position: absolute;
                        top: 8%;
                        right: 5%;
                        display: flex;
                        width: 68px;
                        justify-content: space-between;
                    `}
                >
                    {products.length > slidesToScroll && (
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => scrollPrev()}
                            disabled={!canScrollPrev}
                            data-test-selector="prevBtn"
                        ></Button>
                    )}

                    {products.length > slidesToScroll && (
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => scrollNext()}
                            disabled={!canScrollNext}
                            data-test-selector="nextBtn"
                        ></Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>

            <GridContainer
                {...styles.mainContainer}
                data-test-selector={`productCarousel_${fields.carouselType}${fields.relatedProductType}`}
                data-ga-promo={getGaTagforL1L2(title, "", "")}>
                <GridItem {...styles.carouselGridItem}>
                    <StyledWrapper {...styles.carouselWrapper}>
                        <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                            <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                {products.map(product => (
                                    <StyledWrapper
                                        {...styles.carouselSlide}
                                        key={product.id}
                                        style={{ width: `calc(100% / ${slidesToScroll})` }}
                                    >
                                        <StyledWrapper
                                            {...styles.carouselSlideInner}
                                            id={`product_${product.id}`}
                                            data-test-selector="productContainer"
                                        >
                                            {productInfosByProductId && (
                                                <ADIRecommendedProductCarouselProduct
                                                    carouselId={id}
                                                    product={product}
                                                    productInfo={productInfosByProductId[product.id]}
                                                    showImage={fields.showImage}
                                                    showBrand={showBrandBlocks}
                                                    showTitle={fields.showTitle}
                                                    showPartNumbers={fields.showPartNumbers}
                                                    showPrice={fields.showPrice}
                                                    showAddToCart={fields.showAddToCart}
                                                    showAddToList={fields.showAddToList}
                                                    view="Grid"
                                                    showAvailability={false}
                                                    sourceName={fields.title}
                                                />
                                            )}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
            <span ref={afterCarousel} tabIndex={-1} />
            <StyledWrapper {...styles.dotsContainerWrapper}>
                {slideProducts &&
                    slideProducts.map((_, i) => {
                        const dotColorStyles =
                            i === selectedIndex
                                ? `
                                    background:  ${ADIColors.darkBlue1};
                                    &:hover { background-color: ${ADIColors.darkBlue1};
                                        border: 1px solid ${ADIColors.darkBlue1}; }
                                    &:active {background-color: ${ADIColors.darkBlue1};
                                        border: 1px solid ${ADIColors.darkBlue1}; }
                                `
                                : "";
                        const dotButtonStyles = {
                            css: css`
                                ${styles.dotButton?.css || ""}
                                ${dotColorStyles}
                            `,
                        };
                        // eslint-disable-next-line react/no-array-index-key
                        return <Button key={i} {...dotButtonStyles} onClick={() => onClickDot(i)} />;
                    })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(
        mapStateToProps,
        mapDispatchToProps,
    )(withIsInShell(withTheme(withProduct(ADICategoryRecommendedProductCarousel)))),
    definition: {
        group: "Common",
        icon: "Carousel",
        displayName: "Category Recommended Product",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.scenario,
                displayName: "Scenario",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "New", value: "New" },
                    { displayName: "Recommended for you", value: "UserPick" },
                    { displayName: "Trending In", value: "Trending" },
                ],
                defaultValue: "New",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number Of Products To Display",
                editorTemplate: "IntegerField",
                min: 1,
                max: 20,
                defaultValue: 10,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.seedWithManuallyAssigned,
                displayName: "Seed With Manually Assigned",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Max Number Of Columns",
                editorTemplate: "IntegerField",
                min: 1,
                max: 5,
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showBrandName,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 10,
            },
        ],
    },
};

export default widgetModule;
